import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UseVideoAnalyticsOptions {
  videoName: string;
  duration: number;
}

const MILESTONES = [25, 50, 75, 90];

export function useVideoAnalytics({ videoName, duration }: UseVideoAnalyticsOptions) {
  const firedMilestones = useRef<Set<number>>(new Set());
  const pageUrl = typeof window !== "undefined" ? window.location.pathname : "";

  const logEvent = useCallback(
    async (eventType: string, watchPercent: number, watchSeconds: number) => {
      try {
        await supabase.from("video_events" as any).insert({
          video_name: videoName,
          event_type: eventType,
          watch_percent: Math.round(watchPercent),
          watch_seconds: Math.round(watchSeconds * 100) / 100,
          duration_seconds: Math.round(duration * 100) / 100,
          page_url: pageUrl,
          user_agent: navigator.userAgent,
        });
      } catch {
        // silent fail — analytics should never break UX
      }
    },
    [videoName, duration, pageUrl]
  );

  const onPlay = useCallback(
    (currentTime: number) => {
      const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
      logEvent("play", pct, currentTime);
    },
    [logEvent, duration]
  );

  const onPause = useCallback(
    (currentTime: number) => {
      const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
      logEvent("pause", pct, currentTime);
    },
    [logEvent, duration]
  );

  const onTimeUpdate = useCallback(
    (currentTime: number) => {
      if (duration <= 0) return;
      const pct = (currentTime / duration) * 100;
      for (const m of MILESTONES) {
        if (pct >= m && !firedMilestones.current.has(m)) {
          firedMilestones.current.add(m);
          logEvent("milestone", m, currentTime);
        }
      }
      // Complete event
      if (pct >= 98 && !firedMilestones.current.has(100)) {
        firedMilestones.current.add(100);
        logEvent("complete", 100, currentTime);
      }
    },
    [logEvent, duration]
  );

  const resetMilestones = useCallback(() => {
    firedMilestones.current.clear();
  }, []);

  return { onPlay, onPause, onTimeUpdate, resetMilestones };
}
